import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import {
  type Airport,
  type FiatPrice,
  MultiTicketType,
  type Person,
} from "@b2bportal/air-booking-api";
import type { TripSlice } from "@b2bportal/air-shopping-api";
import { type FailedProduct, Product } from "@b2bportal/purchase-api";
import { trackEvent } from "@hopper-b2b/api";
import {
  AirPriceDropSelectors,
  cartQuoteSelectors,
  commonSelectors,
  ContactSelectors,
  FlightPassengerEventTypesV2,
  FlightPassengerSelectorsV2,
  FlightSelectors,
  getParentState,
  ParentState,
  PriceBreakdownContent,
  type PriceChangeType,
  SeatSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useExperiment } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  CONTINUE_TO_PAYMENT,
  type DisruptionProductType,
  type FlightSummaryInfo,
  type IPerson,
  MODAL_ALERT,
  ModalCategoryType,
  ModalScreens,
  REVIEW_DETAILS,
  TripCategory,
  type TripDetails,
} from "@hopper-b2b/types";
import {
  ActionLink,
  AirlineIcon,
  ButtonWrap,
  ContactSummaryRow,
  ErrorPopup,
  IconName,
  MobileFlightSummaryRowNew,
  MobileFloatingButton,
  MobilePopoverCard,
  PassengerSummaryRow,
} from "@hopper-b2b/ui";
import {
  getSliceIndex,
  PATH_HOME,
  PATH_FLIGHTS_SEARCH,
  useEnablePriceDropProtection,
} from "@hopper-b2b/utilities";
import { Divider } from "@material-ui/core";
import clsx from "clsx";
import { FEATURE_FLAGS } from "../../../../App";
import Close from "../../../../assets/client/close.svg";
import CloseWhite from "../../../../assets/client/darkMode/close-white.svg";
import { BestPriceGuaranteeBanner } from "../../../../components/BestPriceGuaranteeBanner";
import { EnhancedEarnRow } from "../../../../components/EnhancedEarn";
import MobileInfoPopover from "../../../../components/MobileInfoPopover";
import { FlightPriceDropProtectionBanner } from "../../../../components/Slots";
import { useEnhancedEarnAmount } from "../../../../hooks/shared";
import {
  getLocalStorageTrackingEvents,
  useSetLocalStorageTrackingEvents,
} from "../../../../hooks/useFlightLocalStorageTracking";
import { useDarkModePreferred } from "../../../../utils/colors";
import {
  attachTouchDiagnostics,
  logElementStack,
} from "../../../../utils/touchDiagnostics";
import { useTapHandlers } from "../../../../utils/useTapHandlers";
import { Event, type TEvent } from "../../events";
import {
  getConvenienceFeeLineItems,
  getFlightTotalInPrices,
  getIsPriceBreakdownLoading,
  getShopPricingInfo,
  getSummaryLineItems,
  getTripPriceLineItems,
  getSeatMapAvailable,
  getChoosePaymentProperties,
} from "../../selectors";
import { ConnectedBreadcrumbs } from "../Breadcrumbs";
import {
  ConnectedPriceBreakdownContent,
  useConvenienceFeeLineItems,
} from "../PriceBreakdownDropdown";
import { SeatSummaryPanel } from "./components";
import { FareDetailsModal } from "./components/FareDetailsModal";
import { ItineraryDetailsModal } from "./components/ItineraryDetailsModal";
import { PriceSummaryRow } from "./components/PriceSummaryRow";
import { ReviewLayout } from "./components/ReviewLayout";
import "./styles.scss";

export const ConnectedReviewComponent = () => {
  const [openPriceBreakdownModal, setOpenPriceBreakdownModal] =
    useState<boolean>(false);
  const { t } = useI18nContext();
  const history = useHistory();
  const contactInformation = useSelector(
    ContactSelectors.getContactInformation
  );
  const send = useCheckoutSend<TEvent>();
  const stateValue = useSelector(commonSelectors.getStateValue) as string;
  const parentState = getParentState(stateValue);

  const isDarkModeEnabled = useDarkModePreferred();

  const selectedPassengersWithType = useSelector(
    FlightSelectors.mergePassengersAndBreakdownPersons
  );
  const passengers = useSelector(
    FlightPassengerSelectorsV2.getUserPassengersParent
  );

  const seatMapAvailable = useSelector(getSeatMapAvailable);

  const selectedSeatSegments = useSelector(
    SeatSelectors.getSelectedSeatSegments
  );
  const seatSlices = useSelector(SeatSelectors.getSeatSlices);
  const seatedPassengers = useSelector(
    FlightPassengerSelectorsV2.getSeatedPassengersParent
  );
  const isCartLocked = useSelector(cartQuoteSelectors.getCartQuoteIsLocked);
  const priceChange = useSelector(cartQuoteSelectors.getCartQuotePriceChange);
  const unavailableProducts = useSelector(
    cartQuoteSelectors.getUnavailableProducts
  );
  const disruptionOffersProducts = []; // TODO(nfang)
  const flightTotalInPrices = useSelector(getFlightTotalInPrices);
  const tripPriceLineItems = useSelector(getTripPriceLineItems);
  const tripPricingSummaryItems = useSelector(getSummaryLineItems);
  const tripPriceLineItemsWithFee =
    useConvenienceFeeLineItems(tripPriceLineItems);
  const convenienceFeeLineItems = useSelector(getConvenienceFeeLineItems);
  const convenienceFeeTotal = useMemo(
    () => convenienceFeeLineItems?.[0]?.price?.value ?? 0,
    [convenienceFeeLineItems]
  );
  const clientAssets = useSelector(FlightSelectors.getClientAssets);
  const airports = useSelector(FlightSelectors.getAirportMap);
  // Flight Shop Summary Panel
  const tripDetails = useSelector(FlightSelectors.getSelectedTripDetailsParent);
  const tripCategory = useSelector(FlightSelectors.getTripCategoryParent);
  const isPriceBreakdownLoading = useSelector(getIsPriceBreakdownLoading);
  const shopPricingInfo = useSelector(getShopPricingInfo);
  const viewPaymentProperties = useSelector(getChoosePaymentProperties);
  const localStorageTrackingEventsKey = useSelector(
    FlightSelectors.getLocalStorageKey
  );

  const { isEnhancedEarnEnabled } = useEnhancedEarnAmount();

  const initialPassengerPricing = useMemo(
    () => shopPricingInfo?.fare?.[0]?.pricing,
    [shopPricingInfo?.fare]
  );

  const tripCurrency = useMemo(
    () => ({
      currencyCode: flightTotalInPrices?.fiat?.currencyCode,
      currencySymbol: flightTotalInPrices?.fiat?.currencySymbol,
    }),
    [
      flightTotalInPrices?.fiat?.currencyCode,
      flightTotalInPrices?.fiat?.currencySymbol,
    ]
  );

  const ctaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ctaContainerRef.current) {
      logElementStack(ctaContainerRef.current, "flight_review_cta");

      const button = ctaContainerRef.current.querySelector("button");
      if (button) {
        const cleanup = attachTouchDiagnostics(
          button as HTMLElement,
          "flight_review_cta"
        );
        return cleanup;
      }
    }
  }, []);

  const setLocalStorageEvents = useSetLocalStorageTrackingEvents();
  const onBookFlight = useCallback(() => {
    setLocalStorageEvents(localStorageTrackingEventsKey);
    trackEvent({ eventName: CONTINUE_TO_PAYMENT, properties: undefined });
    send(Event.NEXT);
  }, [send, setLocalStorageEvents, localStorageTrackingEventsKey]);

  const { onClick: onTapClick, ...tapPointerHandlers } = useTapHandlers({
    onTap: onBookFlight,
    logName: "flight_review_cta",
  });

  useEffect(() => {
    if (parentState === ParentState.review) {
      // Pass in properties if exist
      const properties = getLocalStorageTrackingEvents(
        localStorageTrackingEventsKey
      );
      trackEvent({
        eventName: REVIEW_DETAILS,
        properties: {
          ...viewPaymentProperties,
          ...properties?.properties,
        },
      });
    }
  }, []);

  // NVH-371 diagnostic: the price-change modal is the only checkout surface
  // not captured by any tracking event, so we cannot tell from data when it
  // fires nor whether the value it promises matches what the cart charges.
  // Emit a one-shot event the moment the modal actually renders, carrying the
  // shop vs quote totals (the values the modal compares + displays). Joined to
  // complete_buy_air on user/route/dates, a decrease (is_increase=false) whose
  // price_quote_total ends up below the charged total is the NVH-371 signature.
  const priceChangeTrackedRef = useRef(false);
  useEffect(() => {
    if (!priceChange?.hasDifference || priceChangeTrackedRef.current) {
      return;
    }
    priceChangeTrackedRef.current = true;
    const predictedTotal =
      (priceChange.predictedTotal ?? 0) + convenienceFeeTotal;
    const priceQuoteTotal =
      (priceChange.priceQuoteTotal ?? 0) + convenienceFeeTotal;
    trackEvent({
      eventName: "price_change_shown",
      properties: {
        ...viewPaymentProperties,
        is_increase: priceChange.isIncrease ?? false,
        predicted_total: predictedTotal,
        price_quote_total: priceQuoteTotal,
        // What the modal displays to the user ("diminuiu para {amount}").
        modal_shown_total: priceQuoteTotal,
        price_change_difference:
          priceChange.difference ?? Math.abs(predictedTotal - priceQuoteTotal),
        price_change_currency: tripCurrency.currencyCode,
      },
    });
  }, [priceChange, convenienceFeeTotal, viewPaymentProperties, tripCurrency]);

  const addSeats = useCallback(() => send(Event.GO_TO_SEAT_SELECTION), [send]);

  // If Cart is locked, then seats have already been added, have to rerun PQ
  const handleEditSeatSelection = useCallback(() => {
    if (isCartLocked) {
      send(Event.GO_TO_CART_QUOTE);
    } else {
      send(Event.EDIT_SEATS_FROM_REVIEW);
    }
  }, [isCartLocked, send]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const tripTotalCurrency = useMemo(() => {
    t("chargedInCurrency", {
      currency: tripCurrency?.currencyCode,
    });
  }, [tripCurrency, t]);

  return (
    <>
      {priceChange?.hasDifference ? (
        <PriceChangeModal
          {...priceChange}
          priceQuoteTotal={
            (priceChange.priceQuoteTotal ?? 0) + convenienceFeeTotal
          }
          predictedTotal={
            (priceChange.predictedTotal ?? 0) + convenienceFeeTotal
          }
          currencyCode={tripCurrency.currencyCode}
        />
      ) : null}
      {unavailableProducts.length > 0 ? (
        <UnavailableProductsModal
          unavailableProducts={unavailableProducts}
          disruptionOffersProducts={disruptionOffersProducts}
        />
      ) : null}
      <ReviewLayout
        handleGoBack={handleGoBack}
        onBookFlight={onBookFlight}
        topContent={<ConnectedBreadcrumbs />}
        clientAssets={clientAssets}
        header={false}
        headerTitle={t("reviewDetails")}
        ctaLabel={t("continueToPayment")}
        rightContent={
          <>
            <ConnectedPriceBreakdownContent />
            {/* Pointer handlers track positions on the wrapper; onClick
                stays on the actual button so a click that doesn't bubble
                through the pointer events still triggers the action. */}
            <div
              ref={ctaContainerRef}
              className="flight-book-mobile-button-container"
              {...tapPointerHandlers}
            >
              <MobileFloatingButton
                className="flight-book-mobile-button"
                onClick={onTapClick}
                wrapperClassName="b2b"
                wide
              >
                {t("continueToPayment")}
              </MobileFloatingButton>
            </div>
          </>
        }
      >
        <FlightShopSummaryPanel tripDetails={tripDetails} airports={airports} />
        <ContactSummaryRow
          contactInfo={contactInformation}
          onEdit={() => send(Event.GO_TO_CONTACT_INFORMATION)}
          hideIcon
        />
        <PassengerSummaryPanel
          passengers={passengers}
          selectedPassengers={selectedPassengersWithType}
        />

        {seatMapAvailable || selectedSeatSegments.length > 0 ? (
          <SeatSummaryPanel
            selectedSeatSegments={selectedSeatSegments}
            tripDetails={tripDetails}
            seatedPassengers={seatedPassengers}
            onEditSeatSelectionClick={handleEditSeatSelection}
            airports={airports}
            addSeats={addSeats}
            seatSlices={seatSlices}
          />
        ) : null}
        <PricingSummaryPanel
          price={flightTotalInPrices?.fiat}
          tripCategory={tripCategory}
          onClick={() => setOpenPriceBreakdownModal(true)}
        />
      </ReviewLayout>
      <MobilePopoverCard
        centered
        fullScreen
        open={openPriceBreakdownModal}
        className={clsx(
          "review-price-breakdown-modal",
          isDarkModeEnabled ? "dark-mode" : null
        )}
        contentClassName="review-price-breakdown-modal-content"
        onClose={() => setOpenPriceBreakdownModal(false)}
        topRightButton={
          <ActionLink
            className="filter-close-button"
            content={
              <img
                className="close-button-icon"
                src={isDarkModeEnabled ? CloseWhite : Close}
                alt=""
              />
            }
            label="Close"
            onClick={() => setOpenPriceBreakdownModal(false)}
          />
        }
      >
        <PriceBreakdownContent
          isLoading={isPriceBreakdownLoading}
          currency={tripCurrency}
          lineItems={tripPriceLineItemsWithFee}
          summaryLineItems={tripPricingSummaryItems}
          header={t("checkoutBreakdown")}
          initialPassengerPricing={initialPassengerPricing}
          tripCategory={tripCategory}
          title={`${t("priceBreakdown")}`}
          currencyString={tripTotalCurrency}
        />
        {isEnhancedEarnEnabled ? (
          <EnhancedEarnRow
            className="review-enhanced-earn"
            totalPrice={flightTotalInPrices?.fiat?.value}
            hasLearnMore
          />
        ) : null}
      </MobilePopoverCard>
    </>
  );
};

interface UnavailableProductsModalProps {
  unavailableProducts: Array<FailedProduct>;
  disruptionOffersProducts: Array<DisruptionProductType>;
}

const UnavailableProductsModal = ({
  unavailableProducts,
  disruptionOffersProducts,
}: UnavailableProductsModalProps) => {
  const { t } = useI18nContext();
  const history = useHistory();

  const toFlightSelection = () => {
    history.push(`${PATH_HOME}${PATH_FLIGHTS_SEARCH}`);
  };

  const [isOpen, setIsOpen] = useState(true);

  const getSubtitle = useCallback(() => {
    const mappedProductToI18nKey = {
      // TODO: Seats will not be added until cart can be mutable with seats
      [Product.Seats]: "Seats",
    };
    const products = unavailableProducts.reduce(
      (listOfProducts, product) => [
        ...(product.id.product === Product.AirDisruption
          ? disruptionOffersProducts.map((disruptionProducts) =>
              t(disruptionProducts)
            )
          : [t(mappedProductToI18nKey[product.id.product])]),
        ...listOfProducts,
      ],
      [] as Array<string>
    );

    return products.length > 1
      ? t("unavailableProductsMultiple", { products })
      : t("unavailableProductSingle", { products });
  }, [unavailableProducts, disruptionOffersProducts, t]);

  return (
    <ErrorPopup
      open={isOpen}
      title={t("genericShopErrorTitle")}
      subtitle={getSubtitle()}
      primaryButtonText={t("continue")}
      primaryOnClick={() => {
        setIsOpen(false);
      }}
      secondaryButtonText={t("flightShopReview.changeFlight")}
      secondaryOnClick={toFlightSelection}
    />
  );
};

interface PriceChangeModalProps extends PriceChangeType {
  currencyCode: string;
}

const PriceChangeModal = (props: PriceChangeModalProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(true);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toFlightSelection = useCallback(() => {
    history.push(`${PATH_HOME}${PATH_FLIGHTS_SEARCH}`);
  }, [history]);

  const isDarkModeEnabled = useDarkModePreferred();

  const primaryButtonText = t("continue");
  const secondaryButtonText = t("flightShopReview.changeFlight");

  useEffect(() => {
    if (isOpen) {
      trackEvent({
        eventName: MODAL_ALERT,
        properties: {
          screen: ModalScreens.FLIGHTS_CHECKOUT,
          category: ModalCategoryType.TROUBLE,
          primary_button: primaryButtonText,
          secondary_button: secondaryButtonText,
        },
      });
    }
  }, [isOpen, primaryButtonText, secondaryButtonText]);

  return (
    <MobileInfoPopover
      isOpen={isOpen}
      onClose={onClose}
      secondaryButtonAction={toFlightSelection}
      secondaryButtonText={secondaryButtonText}
      primaryButtonAction={onClose}
      primaryButtonText={primaryButtonText}
      {...(props.isIncrease
        ? {
            title: t("priceChange.priceIncreaseTitle", {
              amount: formatFiatCurrency({
                value: props.priceQuoteTotal,
                currencyCode: props.currencyCode,
              }),
            }),
            subtitle: t("priceChange.priceIncreaseSubtitle"),
            secondaryButtonAction: toFlightSelection,
            secondaryButtonText: t("flightShopReview.changeFlight"),
          }
        : {
            title: t("priceChange.priceDecreaseTitle", {
              amount: formatFiatCurrency({
                value: props.priceQuoteTotal,
                currencyCode: props.currencyCode,
              }),
            }),
            subtitle: t("priceChange.priceDecreaseSubtitle"),
          })}
      className="price-change-modal"
      closeIcon={
        <img
          alt={t("modalClose.ariaLabel")}
          className={"close-button-icon"}
          src={isDarkModeEnabled ? CloseWhite : Close}
        />
      }
    />
  );
};

interface FlightShopSummaryPanelProps {
  tripDetails: TripDetails;
  airports: { [key: string]: Airport } | undefined;
}

const FlightShopSummaryPanel = (props: FlightShopSummaryPanelProps) => {
  const { t } = useI18nContext();
  const [openFareModal, setOpenFareModal] = useState<boolean>(false);
  const { tripDetails, airports } = props;
  const departureSliceIndex = getSliceIndex(true, tripDetails);
  const departureSlice = tripDetails?.slices[departureSliceIndex];

  const returnSliceIndex = getSliceIndex(false, tripDetails);
  const hasReturnFlight = returnSliceIndex !== -1;

  const origin = useSelector(FlightSelectors.getFlightSearchOrigin);
  const destination = useSelector(FlightSelectors.getFlightSearchDestination);

  const [openItineraryDetails, setOpenItineraryDetails] = useState<
    "departure" | "return" | null
  >(null);

  const ticketType = useSelector(FlightSelectors.getFlightMultiTicketType);

  const hackerFareV2Enabled = useExperiment(FEATURE_FLAGS.HFv2);
  const showHackerFareContent =
    hackerFareV2Enabled && ticketType === MultiTicketType.hackerFare;

  const returnSlice = hasReturnFlight
    ? tripDetails.slices[returnSliceIndex]
    : null;

  const getFlightSummaryInfo = (tripSlice: TripSlice): FlightSummaryInfo => {
    const flightSegment = tripSlice.segmentDetails[0];

    // TODO: fix when BE returns airports in new resume endpoints
    const originLabel =
      airports?.[tripSlice.originCode]?.cityName ||
      origin?.label ||
      tripSlice.originName;
    const destinationLabel =
      airports?.[tripSlice.destinationCode]?.cityName ||
      destination?.label ||
      tripSlice.destinationName;

    return {
      originCity: tripSlice.outgoing ? originLabel : destinationLabel,
      destinationCity: tripSlice.outgoing ? destinationLabel : originLabel,
      departure: tripSlice.departureTime,
      arrival: tripSlice.arrivalTime,
      airlineCode: flightSegment.airlineCode,
      airlineName: flightSegment.airlineName,
      isDeparture: tripSlice.outgoing,
      title: t("originToDestination", {
        origin: originLabel,
        destination: destinationLabel,
      }),
      stops: tripSlice.stops,
    };
  };

  return (
    <>
      <div className="mobile-itinerary-cards-section review-flight-details-section">
        <h3 className="flight-summary-section-header">{t("flights")}</h3>

        <Divider className="mobile-review-flight-book-divider" />
        {/* TODO: Update flight summary row to match Nubank designs */}
        <MobileFlightSummaryRowNew
          flightSummaryInfo={getFlightSummaryInfo(departureSlice)}
          onClick={() => setOpenItineraryDetails("departure")}
          customIcon={
            // TODO: When VI is enabled, add in check to show the double icon
            // <AirplaneDouble className="hackerfare-icon" />
            <AirlineIcon
              airlineCode={departureSlice.segmentDetails[0].airlineCode}
            />
          }
          customExpandIcon={IconName.ChevronDown}
          renderAirlineIcon={false}
          showHackerFareContent={showHackerFareContent}
        />
        {hasReturnFlight && returnSlice ? (
          <MobileFlightSummaryRowNew
            flightSummaryInfo={getFlightSummaryInfo(returnSlice)}
            className="return-flight-details"
            onClick={() => setOpenItineraryDetails("return")}
            customIcon={
              // TODO: When VI is enabled, add in check to show the double icon
              // <AirplaneDouble className="hackerfare-icon" />
              <AirlineIcon
                airlineCode={returnSlice.segmentDetails[0].airlineCode}
              />
            }
            customExpandIcon={IconName.ChevronDown}
            renderAirlineIcon={false}
            showHackerFareContent={showHackerFareContent}
          />
        ) : null}

        <Divider className="mobile-review-flight-book-divider" />
        <div className="review-fare-details-container">
          <p className="fare-details-title">{t("reviewFareDetailsTitle")}</p>
          <ButtonWrap
            className="fare-details-link"
            onClick={() => setOpenFareModal(true)}
          >
            {t("reviewFareDetailsButton")}
          </ButtonWrap>
        </div>
        <Divider className="mobile-review-flight-book-divider" />
      </div>
      <FareDetailsModal
        open={openFareModal}
        onClose={() => setOpenFareModal(false)}
        showHackerFareContent={showHackerFareContent}
      />
      <ItineraryDetailsModal
        openModal={openItineraryDetails}
        onClose={() => setOpenItineraryDetails(null)}
      />
    </>
  );
};

interface PassengerSummaryPanelProps {
  selectedPassengers: Person[];
}

const PassengerSummaryPanel = (props: PassengerSummaryPanelProps) => {
  const { selectedPassengers } = props;
  const send = useCheckoutSend<TEvent>();

  const onEdit = useCallback(
    (passenger: Person) => {
      send({
        type: FlightPassengerEventTypesV2.OPEN_FORM_AND_SET_PASSENGER,
        passenger,
      });
    },
    [send]
  );

  return (
    <div className="mobile-itinerary-cards-section review-passenger-section">
      <div className="mobile-trip-details-passenger-info-container">
        <PassengerSummaryRow
          passengers={selectedPassengers as IPerson[]}
          onEdit={onEdit}
          review
          hideIcon
        />
      </div>

      <Divider className="mobile-review-flight-book-divider" />
    </div>
  );
};

interface PricingSummaryPanelProps {
  price: FiatPrice;
  tripCategory: TripCategory;
  onClick: () => void;
}

const PricingSummaryPanel = (props: PricingSummaryPanelProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const { price, tripCategory, onClick } = props;

  const enabledPriceDropProtection = useEnablePriceDropProtection();
  const priceDropEligibility = useSelector(
    AirPriceDropSelectors.getPriceDropEligibility
  );

  const { isEnhancedEarnEnabled } = useEnhancedEarnAmount();

  const ticketType = useSelector(FlightSelectors.getFlightMultiTicketType);

  const isPDPEligible =
    priceDropEligibility && ticketType !== MultiTicketType.hackerFare;

  const description =
    tripCategory === TripCategory.ROUND_TRIP
      ? t("roundTripCategoryTotal")
      : t("oneWayTripCategoryTotal");

  return (
    <div className="mobile-itinerary-cards-section review-price-section">
      <Divider className="mobile-review-flight-book-divider" />
      <p className="mobile-review-price-title">{t("priceDetails")}</p>
      <Divider className="mobile-review-flight-book-divider" />

      <BestPriceGuaranteeBanner product={Product.Flight} />
      {enabledPriceDropProtection ? (
        <FlightPriceDropProtectionBanner
          showMore
          isEligible={isPDPEligible}
          hideBrand
        />
      ) : null}
      {isEnhancedEarnEnabled ? (
        <EnhancedEarnRow
          className="review-enhanced-earn"
          totalPrice={price.value}
          hasLearnMore
        />
      ) : null}
      {price ? (
        <PriceSummaryRow
          priceString={formatFiatCurrency(price)}
          description={description}
          onClick={onClick}
        />
      ) : null}
    </div>
  );
};
